/**
 * VLT — product page behaviour.
 *
 * Variant changes re-render through the Section Rendering API: the selected
 * options resolve to a variant client-side, then the section is fetched with
 * ?variant= and the price block, pickup block, buy buttons and gallery
 * current-state are swapped in from the server's HTML. Money and stock are
 * never computed in the client.
 *
 * The gallery thumbnails and the lightbox are also handled here; both have
 * working no-JS fallbacks (anchor scroll, no zoom).
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------------
   * Variant selection
   * ---------------------------------------------------------------- */

  function initVariants(root) {
    var variantsScript = root.querySelector('[data-vlt-variants]');
    if (!variantsScript) return;

    var variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    var sectionId = root.dataset.sectionId;
    var productUrl = root.dataset.productUrl;

    function selectedOptions() {
      var values = [];
      root.querySelectorAll('[data-vlt-option]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        var position = parseInt(fieldset.dataset.optionPosition, 10);
        if (checked) values[position - 1] = checked.value;
      });
      return values;
    }

    function findVariant() {
      var chosen = selectedOptions();
      return variants.find(function (variant) {
        return chosen.every(function (value, index) {
          // Positions without a rendered control (single-value options)
          // are undefined here and match any variant.
          return value === undefined || variant.options[index] === value;
        });
      });
    }

    function swap(sourceDoc, selector) {
      var next = sourceDoc.querySelector(selector);
      var current = root.querySelector(selector);
      if (next && current) current.replaceWith(next);
    }

    function onChange() {
      var variant = findVariant();
      if (!variant) return;

      var idInput = root.querySelector('[data-vlt-variant-id]');
      if (idInput) idInput.value = variant.id;

      // Keep the URL shareable at the selected variant.
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Re-render price, pickup, and buy buttons from the server.
      var fetchUrl = productUrl + '?variant=' + variant.id + '&section_id=' + sectionId;
      fetch(fetchUrl)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          swap(doc, '[data-vlt-variant-state]');
          swap(doc, '[data-vlt-pickup]');

          var newAtc = doc.querySelector('[data-vlt-atc]');
          var atc = root.querySelector('[data-vlt-atc]');
          if (newAtc && atc) {
            atc.disabled = newAtc.disabled;
            atc.textContent = newAtc.textContent.trim();
          }
        })
        .catch(function () {
          /* The hidden input is already correct; add-to-cart still works. */
        });

      // Show the variant's featured media if it has one.
      if (variant.featured_media && variant.featured_media.id) {
        showMedia(root, String(variant.featured_media.id));
      }
    }

    root.querySelectorAll('[data-vlt-option-input]').forEach(function (input) {
      input.addEventListener('change', onChange);
    });
  }

  /* ----------------------------------------------------------------
   * Gallery
   * ---------------------------------------------------------------- */

  function showMedia(root, mediaId) {
    var slide = root.querySelector('.vlt-gallery__slide[data-media-id="' + mediaId + '"]');
    var track = root.querySelector('[data-vlt-gallery-track]');
    if (slide && track) {
      track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    }
    root.querySelectorAll('[data-vlt-thumb]').forEach(function (thumb) {
      if (thumb.dataset.mediaId === mediaId) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  function initGallery(root) {
    var track = root.querySelector('[data-vlt-gallery-track]');
    if (!track) return;

    root.querySelectorAll('[data-vlt-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function (event) {
        event.preventDefault();
        showMedia(root, thumb.dataset.mediaId);
      });
    });

    // Keep the active thumbnail in sync while swiping.
    var slides = track.querySelectorAll('.vlt-gallery__slide');
    if ('IntersectionObserver' in window && slides.length > 1) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.target.dataset.mediaId) {
              root.querySelectorAll('[data-vlt-thumb]').forEach(function (thumb) {
                if (thumb.dataset.mediaId === entry.target.dataset.mediaId) {
                  thumb.setAttribute('aria-current', 'true');
                } else {
                  thumb.removeAttribute('aria-current');
                }
              });
            }
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    }

    // Lightbox.
    var lightbox = root.parentElement.querySelector('[data-vlt-lightbox]') ||
      document.querySelector('[data-vlt-lightbox]');
    if (!lightbox || typeof lightbox.showModal !== 'function') return;
    var lightboxImage = lightbox.querySelector('[data-vlt-lightbox-image]');
    var closeButton = lightbox.querySelector('[data-vlt-lightbox-close]');

    root.querySelectorAll('[data-vlt-lightbox-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (lightboxImage) {
          lightboxImage.src = button.dataset.fullSrc;
          var img = button.querySelector('img');
          lightboxImage.alt = img ? img.alt : '';
        }
        lightbox.showModal();
      });
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        lightbox.close();
      });
    }

    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox) lightbox.close();
    });
  }

  function init() {
    document.querySelectorAll('[data-vlt-product]').forEach(function (root) {
      if (root.dataset.vltBound) return;
      root.dataset.vltBound = 'true';
      initVariants(root);
      initGallery(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
